import mapValues from 'lodash.mapvalues'
import isEqual from 'lodash.isequal'
import omit from 'lodash.omit'
import { InitState } from '../../reducers/root/initialState'
import { getValidationInvalid, getValidationSchema } from '../../../services/validation/validationService'
import { RealEstate } from '../../../@types/real-estate-api-types'
import { estateToApi } from '../../../api/realEstate/converters/toApi/estate/estateToApi'
import { convertManualItem } from '../../../services/conversion/conversionServices'
import { ContentStateInterface, DropdownOptionSelection, ManualQuarter } from '../../../@types/types'
import { loadSuggestedTextsFromGenAi } from './suggestedText'
import {
  AddressScreenValidationResult,
  ScreenValidationResult,
  ValidationResult,
  ValidationSchema,
  ValidationStructure,
} from '../../../@types/validation-types'
import { getDefaultLogger } from '../../../services/logger/logger'
import { REQUIRED_VALIDATORS } from '../../../services/validation/rules/validationFunctions'
import { runValidation } from '../../../services/validation/editor/runValidation'
import { AppDispatch } from '../../../@types/store'
import { setPreviousLoadedInsertionData } from '../../reducers/suggestedText/suggestedTextFeature'
import {
  getFeatureSwitchRuntimeValue,
  isFeatureSwitchRuntimeLoadedFromBackend,
} from '../../../utils/features/FeatureSwitchContext'
import { getTextSuggestionsExperimentState } from '../../../utils/features/textSuggestionsExperiment'

function isEqualWithoutDescriptionAttributes(
  newInsertionData: ContentStateInterface,
  insertionData: ContentStateInterface
) {
  const propertyPaths = ['adTitle', 'adDescription', 'adLocation']
  return isEqual(omit(newInsertionData, propertyPaths), omit(insertionData, propertyPaths))
}

function hasValidLabelAndValue(item: DropdownOptionSelection): boolean {
  return item && item.label !== null && item.label !== undefined && item.value !== null && item.value !== undefined
}

function sanitizeInsertionDataForAI(insertionData: ContentStateInterface): ContentStateInterface {
  const sanitized = { ...insertionData }

  if (sanitized.manualRegion && hasValidLabelAndValue(sanitized.manualRegion)) {
    sanitized.manualRegion = {
      ...sanitized.manualRegion,
      label: convertManualItem(sanitized.manualRegion, true).name,
    }
  }

  if (sanitized.manualCity && hasValidLabelAndValue(sanitized.manualCity)) {
    sanitized.manualCity = {
      ...sanitized.manualCity,
      label: convertManualItem(sanitized.manualCity, true).name,
    }
  }

  if (sanitized.manualQuarter && hasValidLabelAndValue(sanitized.manualQuarter)) {
    sanitized.manualQuarter = {
      ...sanitized.manualQuarter,
      label: convertManualItem(sanitized.manualQuarter, true).name,
    } as ManualQuarter
  }

  if (sanitized.manualCountry && hasValidLabelAndValue(sanitized.manualCountry)) {
    sanitized.manualCountry = {
      ...sanitized.manualCountry,
      label: convertManualItem(sanitized.manualCountry, true).name,
    }
  }

  return sanitized
}

export const loadSuggestionsIfNeeded = async (
  dispatch: AppDispatch,
  getState: () => InitState,
  validationSchema: ValidationSchema
) => {
  const flowInformation = getState().flowFeature.flowInformation
  const textSuggestionsFeature = flowInformation.options?.textSuggestionsFeature
  const isTextSuggestionsEnabled = getTextSuggestionsExperimentState({
    isTextSuggestionsFlowOptionEnabled: flowInformation.options?.hasTextSuggestions === true,
    isTextSuggestionsFeatureLoadedFromBackend: isFeatureSwitchRuntimeLoadedFromBackend(),
    textSuggestionsFeatureValue: textSuggestionsFeature
      ? getFeatureSwitchRuntimeValue(textSuggestionsFeature)
      : undefined,
    textSuggestionsFeature,
  }).isTextSuggestionsEnabled

  if (!isTextSuggestionsEnabled) {
    return
  }

  const insertionData = getState().insertionFeature.insertionData
  const prevInsertionData = getState().suggestedTextFeature.prevInsertionData
  const addressFeature = getState().addressFeature
  const insertionIdentification = getState().insertionFeature.insertionIdentification

  if (isEqualWithoutDescriptionAttributes(insertionData, prevInsertionData)) {
    return
  }

  dispatch(setPreviousLoadedInsertionData({ insertionData: insertionData }))

  const schemaWithoutRequiredFieldsExceptAddressAndWithoutContactValidation: ValidationSchema = {
    ...mapValues(
      getValidationSchema({ ...validationSchema, contactInfo: undefined }, insertionData, addressFeature),
      (screenSchema) =>
        mapValues(screenSchema, (validators) =>
          validators?.filter((validator) => !REQUIRED_VALIDATORS.includes(validator))
        )
    ),
    address: validationSchema.address,
  }

  const validationResult = runValidation(
    insertionData,
    addressFeature,
    schemaWithoutRequiredFieldsExceptAddressAndWithoutContactValidation
  )
  const isInternalValidationInvalid = getValidationInvalid<
    ValidationResult,
    ScreenValidationResult<AddressScreenValidationResult>
  >(validationResult, (value) => value.fields as ValidationStructure)

  if (isInternalValidationInvalid) {
    return
  }
  let realEstate: RealEstate
  try {
    // Use sanitized insertion data for AI suggestions to avoid sending duplicated district names
    // Backend API calls will continue using original district names through the normal save flow
    const dataForApi = sanitizeInsertionDataForAI(insertionData)
    realEstate = estateToApi(insertionIdentification, dataForApi, addressFeature, flowInformation)
  } catch (e) {
    /* istanbul ignore next */
    getDefaultLogger().warn('Failed to transform data to api to load text suggestions.', e, flowInformation.metaData, {
      emailLength: insertionData.email ? String(insertionData.email).length : 0,
      lastNameLength: insertionData.lastName ? String(insertionData.lastName).length : 0,
      salutation: insertionData.salutation,
    })
    return
  }

  const tasks = getSuggestionTasks(insertionData, realEstate, dispatch)

  if (tasks.length > 0) {
    await Promise.all(tasks)
  }
}

function getSuggestionTasks(insertionData: ContentStateInterface, realEstate: RealEstate, dispatch: AppDispatch) {
  const tasks = []
  const hasCityPostcode = cityAndPostcodeDefined(insertionData)
  const hasGeoCoords = hasGeoCoordinates(realEstate)

  if (hasCityPostcode && hasGeoCoords) {
    tasks.push(dispatch(loadSuggestedTextsFromGenAi(realEstate)))
  }

  return tasks
}

function cityAndPostcodeDefined(insertionData?: ContentStateInterface) {
  return insertionData && insertionData.city && insertionData.postcode
}

function hasGeoCoordinates(realEstate: RealEstate) {
  const geo = realEstate.address?.geoCoordinates

  if (!geo) {
    return false
  }

  const { latitude, longitude, lambertEuCoordinatesX, lambertEuCoordinatesY } = geo

  return (
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    typeof lambertEuCoordinatesX === 'number' &&
    typeof lambertEuCoordinatesY === 'number'
  )
}
